import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
// import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext"; // Valtech Custom Code
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useIsSignedIn } from "@store/Hooks/Common";
import { TYPOGRAPHY } from "@store/theme/design";
import { actionLinkStyles } from "@store/theme/mixins/typography";

type Props = ProductCardSelections & HasProductContext;
/* Valtech Custom Code */

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // Valtech Custom Code
    informationsAndActionsWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productOptionsLinkWrapper?: InjectableCss;
    productOptionsLink?: LinkPresentationProps;
    productStartingAtText?: TypographyPresentationProps;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
        `,
    },
    informationsAndActionsWrapper: {
        css: css`
            padding: 20px 30px 0;
        `,
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            margin-top: 4px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
    productStartingAtText: {
        size: TYPOGRAPHY.size.smaller,
        weight: TYPOGRAPHY.weight.bold,
    },
    productPrice: {
        wrapper: css`
            width: auto;
        `,
        price: {
            priceText: {
                size: TYPOGRAPHY.size.smaller,
            },
        },
    },
    productOptionsLink: {
        css: css`
            ${actionLinkStyles()}
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    // product,  // Valtech Custom Code
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    // Valtech Custom Code
    productContext,
}) => {
    const isSignedIn = useIsSignedIn(); // Valtech Custom Code
    const hasVariants = productContext.product.isVariantParent; // Valtech Custom Code
    const productDetailPath = productContext.productInfo.productDetailPath; // Valtech Custom Code

    /* Valtech Custom Code */
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${productContext.product.id}`}>
            {showImage ? (
                <StyledWrapper {...styles.imageWrapper}>
                    {productContext.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    <ProductListProductImage
                        showImage={showImage}
                        showCompare={showCompare}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                    />
                </StyledWrapper>
            ) : null}
            <StyledWrapper {...styles.informationsAndActionsWrapper}>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={showAvailability}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                />
                {isSignedIn ? (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        {hasVariants ? (
                            <Typography {...styles.productStartingAtText}>
                                {`${translate("Starting at")} ${
                                    productContext.productInfo.pricing?.actualPriceDisplay
                                }`}
                            </Typography>
                        ) : (
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPrice}
                            />
                        )}
                    </StyledWrapper>
                ) : null}
                {hasVariants ? (
                    <StyledWrapper {...styles.productOptionsLinkWrapper}>
                        <Link
                            {...styles.productOptionsLink}
                            href={productDetailPath}
                            data-test-selector="productDescriptionLink"
                        >
                            {translate("Click to view options")}
                        </Link>
                    </StyledWrapper>
                ) : null}
                {isSignedIn && showPrice ? (
                    <ProductListActions showAddToList={showAddToList} showPrice={false} />
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
    /* Valtech Custom Code */
};

export default withProductContext(ProductListProductGridCard); // Valtech Custom Code
